import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import type { VibeAnalysisResult } from './types';

// Copied to /mediapipe-wasm at build time by webpack.config.web.js's
// CopyWebpackPlugin entry — MediaPipe's FilesetResolver fetches these WASM
// runtime files by URL at startup, they're not bundled inline via `import`.
const WASM_BASE_PATH = '/mediapipe-wasm';

// The face_landmarker.task model weights (a few MB) have no local/bundlable
// copy in the npm package — only the WASM runtime does. This is MediaPipe's
// own official model-hosting URL (the standard, documented way every
// MediaPipe web app loads this model; there is no offline alternative
// shipped anywhere). Flagged explicitly in the Phase 4 report: Vibe
// Verification on web has a one-time runtime dependency on
// storage.googleapis.com to fetch this file (cached by the browser after
// first use), unlike everything else in this app which is fully
// self-contained.
const MODEL_ASSET_PATH = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task';

let landmarkerPromise: Promise<FaceLandmarker> | null = null;

async function createLandmarker(delegate: 'GPU' | 'CPU'): Promise<FaceLandmarker> {
  const vision = await FilesetResolver.forVisionTasks(WASM_BASE_PATH);
  return FaceLandmarker.createFromOptions(vision, {
    baseOptions: { modelAssetPath: MODEL_ASSET_PATH, delegate },
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: true,
    runningMode: 'IMAGE',
    // Only needs to distinguish "exactly one" from "more than one" — mirrors
    // native's multiple_faces guard (poseVerificationConfig's PoseDebugSnapshot
    // consumer only ever checks faceCount > 1).
    numFaces: 2,
  });
}

function getLandmarker(): Promise<FaceLandmarker> {
  if (!landmarkerPromise) {
    // GPU delegate is faster but unsupported in some browsers/contexts (no
    // WebGL2, headless/software rendering) — fall back to CPU rather than
    // leaving verification permanently broken there.
    landmarkerPromise = createLandmarker('GPU').catch(() => createLandmarker('CPU'));
  }
  return landmarkerPromise;
}

async function loadImageBitmap(imagePath: string): Promise<ImageBitmap> {
  const response = await fetch(imagePath);
  const blob = await response.blob();
  return createImageBitmap(blob);
}

/**
 * Decomposes a column-major 4x4 rotation matrix into yaw/pitch/roll degrees,
 * matching ML Kit's headEulerAngleX(pitch)/Y(yaw)/Z(roll) convention as
 * closely as a completely different detector reasonably can. Sign
 * conventions are matched to native's LEFT_YAW_SIGN/RIGHT_YAW_SIGN constants
 * as a starting point only — MediaPipe operates on the raw, un-mirrored
 * camera frame, which may not share ML Kit + VisionCamera's exact frame
 * orientation, so this needs a real-webcam confirmation pass (flagged in the
 * Phase 4 report) to confirm LEFT/RIGHT aren't swapped in practice.
 */
function matrixToEulerDegrees(data: number[]): { pitch: number; yaw: number; roll: number } {
  // Column-major 4x4: element (row, col) is at data[col * 4 + row].
  const m31 = data[2];
  const m32 = data[6];
  const m33 = data[10];
  const m21 = data[1];
  const m11 = data[0];
  const m23 = data[9];
  const m22 = data[5];

  let pitchRad: number;
  let yawRad: number;
  let rollRad: number;
  if (Math.abs(m31) < 0.999999) {
    pitchRad = Math.atan2(-m32, m33);
    yawRad = Math.asin(Math.max(-1, Math.min(1, m31)));
    rollRad = Math.atan2(-m21, m11);
  } else {
    // Gimbal lock (near-90° yaw) — roll and pitch aren't separable; this
    // only matters right at the edge of PROFILE_YAW_MAX_DEG, well past what
    // this flow ever asks for.
    pitchRad = Math.atan2(m23, m22);
    yawRad = m31 > 0 ? Math.PI / 2 : -Math.PI / 2;
    rollRad = 0;
  }
  const toDeg = (rad: number) => (rad * 180) / Math.PI;
  return { pitch: toDeg(pitchRad), yaw: toDeg(yawRad), roll: toDeg(rollRad) };
}

/** Web counterpart to the native TonyteVibeVerification module — same signature, MediaPipe FaceLandmarker instead of ML Kit. `imagePath` is a data:/blob: URL here rather than a native file path. */
export async function analyzeVerificationFrame(imagePath: string): Promise<VibeAnalysisResult | null> {
  try {
    const landmarker = await getLandmarker();
    const bitmap = await loadImageBitmap(imagePath);
    let result;
    try {
      result = landmarker.detect(bitmap);
    } finally {
      bitmap.close();
    }
    const faceCount = result.faceLandmarks?.length || 0;
    if (faceCount === 0) {
      return { faceCount: 0, faceDetected: false };
    }
    const matrix = result.facialTransformationMatrixes?.[0];
    if (!matrix) {
      return { faceCount, faceDetected: true };
    }
    const { pitch, yaw, roll } = matrixToEulerDegrees(matrix.data);
    // [VibePoseDebug][Web][Matrix] — the raw, pre-shared-config numbers this
    // platform boundary produces, BEFORE poseVerificationConfig.ts's shared
    // LEFT_YAW_SIGN/RIGHT_YAW_SIGN gates ever see them. The generic
    // '[VibePoseDebug][JS] received'/'gates' logs already fired from
    // useVibeVerificationStateMachine.ts (unchanged, shared with native)
    // show the POST-gate pass/fail; this line is what to read to determine
    // whether LEFT/RIGHT are swapped on web specifically — turn your head to
    // YOUR left and confirm the sign here matches what
    // poseVerificationConfig.ts's doc comment says native uses (positive =
    // turned to their own left). If it's inverted, negate `yaw` in this
    // function only — never touch poseVerificationConfig.ts's constants,
    // which are native's own empirically-verified values.
    if (__DEV__) console.log('[VibePoseDebug][Web][Matrix] raw yaw/pitch/roll', { yaw, pitch, roll });
    return {
      faceCount,
      faceDetected: true,
      headEulerAngleX: pitch,
      headEulerAngleY: yaw,
      headEulerAngleZ: roll,
    };
  } catch (error) {
    if (__DEV__) console.log('[VibePoseDebug][Web] analyzeVerificationFrame failed', error);
    return null;
  }
}

export async function releaseVerificationResources(): Promise<void> {
  if (!landmarkerPromise) return;
  const pending = landmarkerPromise;
  landmarkerPromise = null;
  const landmarker = await pending.catch(() => null);
  landmarker?.close();
}
